import { BottomAction } from '@euler/app/components/BottomAction';
import { WiredSearchInput } from '@euler/app/components/SearchInput';
import { StackLayout } from '@euler/app/components/StackLayout';
import { KeyboardSpacerView } from '@euler/app/components/keyboard/KeyboardSpacerView';
import { InfiniteListFooter } from '@euler/app/components/list/InfiniteListFooter';
import { Typography } from '@euler/app/components/typography';
import type { ProductNavParams } from '@euler/app/flows/products/screens';
import { useSiteInspectionNavigatorContext } from '@euler/app/flows/task/pages/inspection/site-inspection/SiteInspectionContext';
import { StatusColors } from '@euler/components/Colors';
import { Icon } from '@euler/components/Icomoon';
import { Img } from '@euler/components/image/AdvancedImage';
import { PredefinedHitSlops } from '@euler/functions/predefinedHitSlops';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useInfiniteList } from '@euler/functions/useInfiniteList';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { ProductWithContractId } from '@euler/model/product';
import { TaskProductManifestItem } from '@euler/model/task-detail';
import { useServiceFactory } from '@euler/services/factory';
import { SafeHaptics, delayed, idEquals, onErrorIgnore } from '@euler/utils';
import { useCreation, usePersistFn } from '@euler/utils/hooks';
import { BottomSheetFlatList } from '@gorhom/bottom-sheet';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import {
  FlatList,
  ListRenderItem,
  Platform,
  RefreshControl,
  StyleProp,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
  BehaviorSubject,
  Observable,
  debounceTime,
  distinctUntilChanged,
  skip,
} from 'rxjs';
import { useTheme } from 'styled-components';

const keyExtractor = (item: ProductWithContractId) =>
  item.sourceContractId + ':' + String(item.id);

const kLimit = 20;

export const AddProductPage = wrapNavigatorScreen(
  ({
    subject,
    onGetManifest$,
    onDone,
  }: {
    subject: string;
    onGetManifest$: () => Observable<TaskProductManifestItem[]>;
    onDone?: (selection: ProductWithContractId[]) => void;
  }) => {
    const navigation = useNavigation<StackNavigationProp<ProductNavParams>>();
    const siteInspectionNavContext = useSiteInspectionNavigatorContext();

    const { link: linkColor } = useTheme();
    const { rpx } = useResponsiveContext();
    const { bottom } = useSafeAreaInsets();
    const { productService } = useServiceFactory();

    const query$ = useCreation(
      () => new BehaviorSubject<string>(subject),
      [subject],
    );

    const bottomComponent$ = siteInspectionNavContext?.bottomComponent$;
    const [selection, setSelection] = useState<ProductWithContractId[]>([]);

    const { data, status, replace, fetchWithoutRefresh, fetchMore } =
      useInfiniteList({
        limit: kLimit,
        keyExtractor,
        query: async (offset: number, keyword: string | undefined) => {
          return await productService.searchProducts(
            {
              keyword: keyword?.trim(),
            },
            offset,
            kLimit,
          );
        },
      });

    const onRefresh = usePersistFn(() => {
      __DEV__ && console.log('refresh... ');
      replace(query$.getValue()).catch(onErrorIgnore);
    });

    const onViewManifestPress = useCallback(() => {
      navigation.navigate('_productManifest', { items$: onGetManifest$() });
    }, [navigation, onGetManifest$]);

    const onDonePress = usePersistFn(() => {
      onDone?.(selection);
    });

    useEffect(() => {
      void fetchWithoutRefresh(subject);
      const sub = query$
        .pipe(debounceTime(500), distinctUntilChanged(), skip(1))
        .subscribe(keyword => {
          void fetchWithoutRefresh(keyword);
        });
      return () => sub.unsubscribe();
    }, [fetchWithoutRefresh, query$, subject]);

    useEffect(() => {
      navigation.setOptions({
        title: `产品推荐\n${subject}`,
        headerRight: () => (
          <TouchableOpacity
            onPress={onViewManifestPress}
            css={`
              padding: 0 ${rpx(15)}px;
              flex-direction: row;
              justify-content: flex-end;
              align-items: center;
            `}
          >
            <Icon name="clipboard" color={linkColor} size={rpx(13)} />
            <Typography.Label
              regular
              color={linkColor}
              size={rpx(13)}
              ml={rpx(3)}
            >
              产品清单
            </Typography.Label>
          </TouchableOpacity>
        ),
      });
    }, [linkColor, navigation, onViewManifestPress, rpx, subject]);

    const hasSelection = selection.length > 0;

    const footer = useMemo(
      () => (
        <BottomAction containerStyle={{ paddingBottom: 0 }}>
          <BottomAction.Button
            variant={hasSelection ? 'primary' : 'default'}
            text="完成"
            disabled={!hasSelection}
            onPress={onDonePress}
          />
        </BottomAction>
      ),
      [hasSelection, onDonePress],
    );

    useEffect(() => {
      if (bottomComponent$ != null) {
        delayed(() => {
          bottomComponent$.next(footer);
        }, 200);
      }
    }, [bottomComponent$, footer]);

    const onProductSelected = usePersistFn(
      (product: ProductWithContractId, selected: boolean) => {
        if (selected) {
          setSelection(l => [...l, product]);
        } else {
          setSelection(l => l.filter(x => !idEquals(x.id, product.id)));
        }
      },
    );

    const onProductPress = usePersistFn((product: ProductWithContractId) => {
      navigation.navigate('_productDetail', { id: product.id });
    });

    const renderItem: ListRenderItem<ProductWithContractId> = useCallback(
      ({ item }) => {
        return (
          <Cell
            product={item}
            selected={selection.some(x => idEquals(x.id, item.id))}
            onSelect={onProductSelected}
            onPress={onProductPress}
            css={`
              padding: ${rpx(8)}px ${rpx(10)}px;
            `}
          />
        );
      },
      [onProductPress, onProductSelected, rpx, selection],
    );

    const mergedData = useMemo(() => {
      if (!selection.length) {
        return data;
      }
      const list = [...data];
      const ids = new Set(list.map(x => String(x.id)));
      for (const item of selection) {
        if (!ids.has(String(item.id))) {
          list.unshift(item);
        }
      }
      return list;
    }, [data, selection]);

    const ListComponent =
      siteInspectionNavContext?.navigatorContainerType === 'bottomsheet'
        ? BottomSheetFlatList
        : FlatList;

    return (
      <>
        <View
          css={`
            flex: 1;
          `}
        >
          <WiredSearchInput
            placeholder="输入关键词搜索相关产品"
            query$={query$}
            instanceSearchEnabled
            placeholderTextColor="#ccc"
            isLoading={status.isLoading && !status.isRefreshing}
            onSearch={keyword => fetchWithoutRefresh(keyword)}
            style={{
              borderRadius: 0,
            }}
            css={`
              border-top-width: 0.5px;
              border-top-color: #ddd;
              border-bottom-width: 0.5px;
              border-bottom-color: #ddd;
              margin-top: ${rpx(10)}px;
              margin-bottom: ${rpx(10)}px;
            `}
          />
          <ListComponent
            data={mergedData}
            keyExtractor={keyExtractor}
            renderItem={renderItem}
            style={{ flex: 1 }}
            onEndReachedThreshold={0.1}
            onEndReached={() => {
              status.loadedDataLength > 0 && void fetchMore();
            }}
            refreshControl={
              Platform.OS === 'web' ? undefined : (
                <RefreshControl
                  refreshing={status.isRefreshing ?? false}
                  onRefresh={onRefresh}
                />
              )
            }
            ListFooterComponent={
              <>
                <InfiniteListFooter status={status} />
                <KeyboardSpacerView />
              </>
            }
            contentContainerStyle={{
              flexGrow: 1,
              backgroundColor: '#fff',
              padding: 0,
              paddingBottom: rpx(68) + bottom,
            }}
          />
        </View>
        {bottomComponent$ == null && footer}
      </>
    );
  },
);

const Cell = memo(
  ({
    style,
    product,
    selected,
    onSelect,
    onPress,
  }: {
    product: ProductWithContractId;
    selected?: boolean;
    style?: StyleProp<ViewStyle>;
    onSelect?: (product: ProductWithContractId, selected: boolean) => void;
    onPress?: (product: ProductWithContractId) => void;
  }) => {
    const { link: linkColor } = useTheme();
    const { rpx } = useResponsiveContext();
    const handleSelection = usePersistFn(() => {
      SafeHaptics.selection();
      onSelect?.(product, !selected);
    });
    const handlePress = usePersistFn(() => {
      onPress?.(product);
    });
    return (
      <StackLayout
        direction="row"
        style={[style, selected ? { backgroundColor: '#f7f7f7' } : undefined]}
        gap={rpx(15)}
        alignItems="center"
      >
        <TouchableOpacity
          onPress={handleSelection}
          hitSlop={PredefinedHitSlops.large}
          css={`
            flex: 1;
          `}
        >
          <StackLayout direction="row" alignItems="center" gap={rpx(10)}>
            <Icon
              name={selected ? 'checkmark-circle' : 'radio_button_off'}
              size={rpx(25)}
              color={selected ? StatusColors.Success : '#ccc'}
            />
            <Img
              uri={product.coverUrl}
              css={`
                width: ${rpx(56)}px;
                height: ${rpx(56)}px;
                border-radius: ${rpx(8)}px;
                background-color: #ccc;
                overflow: hidden;
              `}
            />
            <View
              css={`
                flex: 1;
              `}
            >
              <Typography.Label bold color="#333" size={rpx(15)}>
                {product.name}
              </Typography.Label>
              <StackLayout
                direction="row"
                justifyContent="flex-start"
                alignItems="center"
                gap={rpx(8)}
              >
                <Typography.Label
                  light
                  color="#999"
                  size={rpx(13)}
                  lineHeight={rpx(24)}
                >
                  {`品牌: ${product.brandName}`}
                </Typography.Label>
                <Typography.Label
                  light
                  color="#999"
                  size={rpx(13)}
                  lineHeight={rpx(24)}
                >
                  {`代理商: ${product.agentName}`}
                </Typography.Label>
              </StackLayout>
              <Typography.Label
                light
                color="#555"
                size={rpx(11)}
                lineHeight={rpx(16)}
              >
                {product.title ?? '暂无产品亮点说明'}
              </Typography.Label>
            </View>
          </StackLayout>
        </TouchableOpacity>
        <TouchableOpacity
          onPress={handlePress}
          css={`
            align-self: stretch;
          `}
        >
          <StackLayout
            direction="row"
            justifyContent="flex-end"
            alignItems="center"
            css={`
              flex: 1;
            `}
          >
            <Icon name="information-outline" size={rpx(18)} color={linkColor} />
            <Icon name={'chevron-right'} size={rpx(26)} color={'#ccc'} />
          </StackLayout>
        </TouchableOpacity>
        <View
          css={`
            position: absolute;
            left: ${rpx(8)}px;
            bottom: 0;
            right: ${rpx(-8)}px;
            height: 0.5px;
            background-color: #ddd;
          `}
        />
      </StackLayout>
    );
  },
);
